import React, { useState, useContext, useRef } from 'react';

import { ALERT_TYPE, CUENTA_STATE, MASK_FORMAT, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from '../../config/apis';
import { ServerRequest } from '../../utils/apiweb';
import { useFilterPersistence, useNavigateWithFilters } from '../../components/hooks';
import { createSearchWithSavedFilters } from '../../utils/searchWithSavedFilters';
import {
    Loading,
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch
} from '../../components/common';
import DataTaggerModalApi from '../../components/controls/DataTaggerModalApi';
import { ManagedContext } from '../../context/custom';
import { MESSAGE_MODALS } from '../../context/custom/MessageModalContext';
import { useLista } from '../../components/hooks/useLista';
import { usePermissions } from '../../components/hooks/usePermissions';
import { PERMISSIONS } from '../../consts/permissions';
import {TIPS} from "../../consts/tips";
import { parseSerializedIds } from '../../utils/convert';
import { buildComercioEntityInit } from '../ComercioView/utils';

function ComerciosView() {

    //hooks
    const { showMessageModal } = useContext(ManagedContext);

    const [state, setState] = useState({
        loading: false,
        dataTagger: {
            showModal: false,
            idEntidad: null
        },
        list: []
    });

    const searchRef = useRef();
    const tableRef = useRef();
    const [tableVisible, setTableVisible] = useState(false);
    const [searchMessage, setSearchMessage] = useState(null);

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.COMERCIO_EDIT);
    const canReactivar = hasPermission(PERMISSIONS.CUENTA_EDIT);

    const tabsConfig = [
        {
            key: 'cuenta',
            title: 'Cuenta',
            fields: ['numeroCuenta', 'idPersona', 'idEstadoCuenta', 'etiqueta', 'idsTipoCondicionEspecial', 'idsTipoRecargoDescuento'],
            buildUrl: (filters) =>
                `/cuenta/filter?` +
                `numeroCuenta=${encodeURIComponent(filters.numeroCuenta)}&` +
                `idEstadoCuenta=${filters.idEstadoCuenta}&` +
                `numeroWeb=&numeroDocumento=&` +
                `idPersona=${filters.idPersona}&` +
                `etiqueta=${encodeURIComponent(filters.etiqueta)}&` +
                `idsTipoCondicionEspecial=${encodeURIComponent(parseSerializedIds(filters.idsTipoCondicionEspecial))}&` +
                `idsTipoRecargoDescuento=${encodeURIComponent(parseSerializedIds(filters.idsTipoRecargoDescuento))}`
        },
        {
            key: 'datosComercio',
            title: 'Datos del comercio',
            fields: ['numeroCuit', 'rubro', 'nombreFantasia', 'cuentaInmueble', 'direccion'],
            buildUrl: (filters) =>
                `/datos/filter?` +
                `cuentaInmueble=${encodeURIComponent(filters.cuentaInmueble)}&` +
                `rubro=${encodeURIComponent(filters.rubro)}&` +
                `nombreFantasia=${encodeURIComponent(filters.nombreFantasia)}&` +
                `numeroCuit=${(filters.numeroCuit??'').replace(/-/g,'')}&` +
                `direccion=${encodeURIComponent(filters.direccion || '')}`
        }
    ];

    //callbacks
    const callbackNoSuccess = (response) => {
        response.json()
            .then((error) => {
                const message = error.message;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                const message = 'Error procesando respuesta: ' + error;
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                setState(prevState => ({ ...prevState, loading: false }));
            });
    };

    const callbackError = (error) => {
        const message = 'Error procesando solicitud: ' + error.message;
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
        setState(prevState => ({ ...prevState, loading: false }));
    };

    const SearchComerciosWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.COMERCIO,
        tabsConfig,
        callbackNoSuccess,
        callbackError,
        setSearchMessage
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchComerciosWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    //definiciones
    const cellA = (props) => !canEdit ? '' : (
        <div className='action'>
            <div onClick={() => handleClickComercioAdd()} className="link">
                <span className="material-symbols-outlined" title="Nuevo">add</span>
            </div>
        </div>
    );

    const cellVMR = (props) => (
        <div className='action'>
            <div onClick={() => handleClickComercioView(props.value)} className="link">
                <span className="material-symbols-outlined" title="Ver">search</span>
            </div>
            {canEdit && props.row.original.idEstadoCuenta !== CUENTA_STATE.INACTIVA && (
                <>
                    <div onClick={() => handleClickComercioModify(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Modificar">stylus</span>
                    </div>
                    <div onClick={() => handleClickComercioRemove(props.value)} className="link">
                        <span className="material-symbols-outlined" title="Eliminar">delete</span>
                    </div>
                </>
            )}
            {canReactivar && props.row.original.idEstadoCuenta === CUENTA_STATE.INACTIVA && (
                <div onClick={() => handleClickReactivar(props.row.original.idCuenta, props.row.original.numeroCuenta)} className="link">
                    <span className="material-symbols-outlined" title="Reactivar">unarchive</span>
                </div>
            )}
            <div onClick={() => handleClickComercioDataTagger(props.value)} className="link">
                <span className="material-symbols-outlined" title="Información adicional">info</span>
            </div>
        </div>
    );

    const [, getRowLista] = useLista({
        listas: ['EstadoCuenta'],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'EstadoCuenta',
            timeout: 0
        }
    });

    const getDescEstadoCuenta = (id) => {
        const row = getRowLista('EstadoCuenta', id);
        return (row) ? row.nombre : '';
    };

    const tableColumns = [
        { Header: 'N° cuenta', accessor: 'numeroCuenta', width: '15%' },
        { Header: 'Cuenta inmueble', accessor: 'cuentaInmueble', width: '15%' },
        { Header: 'Número CUIT', accessor: 'numeroCuit', width: '12%' },
        { Header: 'Rubro', accessor: 'rubro', width: '12%' },
        { Header: 'Nombre de fantasía', accessor: 'nombreFantasia', width: '19%' },
        { Header: 'Razón social', accessor: 'razonSocial', width: '19%' },
        {
            Header: 'Estado cuenta',
            Cell: (props) => getDescEstadoCuenta(props.value),
            accessor: 'idEstadoCuenta',
            width: '12%'
        },
        {
            Header: cellA,
            Cell: cellVMR,
            id: 'abm',
            accessor: 'id',
            width: '8%',
            disableGlobalFilter: true,
            disableSortBy: true
        }
    ];

    //handles
    const handleClickComercioAdd = () => {
        showMessageModal({
            ...MESSAGE_MODALS.CREAR,
            onConfirm: () => CreateComercio(),
        });
    };
    function CreateComercio() {
        setState(prevState => ({ ...prevState, loading: true }));
        const callbackSuccess = (response) => {
            response.json()
                .then((row) => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    navigateWithFilters(`/comercio/${OPERATION_MODE.EDIT}/${row.comercio.id}`);
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando respuesta: ' + error);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };
        ServerRequest(
            REQUEST_METHOD.POST, null, true,
            APIS.URLS.COMERCIO, null,
            buildComercioEntityInit(),
            callbackSuccess, callbackNoSuccess, callbackError
        );
    }
    const handleClickComercioView = (id) => {
        navigateWithFilters('/comercio/' + OPERATION_MODE.VIEW + '/' + id);
    };
    const handleClickComercioModify = (id) => {
        navigateWithFilters('/comercio/' + OPERATION_MODE.EDIT + '/' + id);
    };
    const handleClickComercioRemove = (id) => {
        const comercio = state.list.find(x => x.id === id);
        showMessageModal({
            ...MESSAGE_MODALS.BORRAR,
            message: `¿Está seguro de borrar el número de cuenta ${comercio.numeroCuenta}?`,
            onConfirm: () => RemoveComercio(id),
        });
    };
    const handleClickReactivar = (idCuenta, numeroCuenta) => {
        showMessageModal({
            title: "Confirmación",
            message: `¿Está seguro de reactivar el número de cuenta ${numeroCuenta}?`,
            onConfirm: () => ReactivarCuenta(idCuenta),
        });
    };
    const handleClickComercioDataTagger = (id) => {
        setState(prevState => ({ ...prevState, dataTagger: { showModal: true, idEntidad: id } }));
    };

    //funciones
    function SearchComercios() {
        return SearchComerciosWithSavedFilters(getCurrentFilters());
    }

    function RemoveComercio(id) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    SearchComercios();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.COMERCIO,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    function ReactivarCuenta(idCuenta) {
        setState(prevState => ({ ...prevState, loading: true }));

        const callbackSuccess = (response) => {
            response.json()
                .then(() => {
                    setState(prevState => ({ ...prevState, loading: false }));
                    ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'La cuenta fue reactivada correctamente');
                    SearchComercios();
                })
                .catch((error) => {
                    const message = 'Error procesando respuesta: ' + error;
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, message);
                    setState(prevState => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl = `/reactivar/${idCuenta}`;

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CUENTA,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    }

    const dataTaggerRow = state.list.find(x => x.id === state.dataTagger.idEntidad)
    const dataTaggerRegistro = dataTaggerRow ? (dataTaggerRow.nombreFantasia || dataTaggerRow.razonSocial || '') : ''

    return (
        <>
            <Loading visible={state.loading} />

            {state.dataTagger.showModal && (
                <DataTaggerModalApi
                    title="Información adicional de comercio"
                    registro={dataTaggerRegistro}
                    tipName={TIPS.NAME.MODALS.INFORMACION_ADICIONAL_COMERCIO_MODAL}
                    entidad="Comercio"
                    idEntidad={state.dataTagger.idEntidad}
                    disabled={!canEdit}
                    onConfirm={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                    onDismiss={() => {
                        setState(prevState => ({ ...prevState, dataTagger: { showModal: false, idEntidad: null } }));
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: 'Tributos' }, { title: 'Administración de comercios' }]}
                onAddClick={canEdit ? handleClickComercioAdd : null}
                tableVisible={tableVisible}
            />

            <section className='section-accordion'>
                <WrapperAdvancedSearch
                    ref={searchRef}
                    onSearch={SearchComercios}
                    requireFilter={true}
                    message={searchMessage}
                    onClear={() => {
                        setState(prevState => ({ ...prevState, list: [] }));
                        setTableVisible(false);
                    }}
                    tabs={tabsConfig}
                    filters={[
                        { title: 'Número de cuenta', field: 'numeroCuenta', type: 'string', size: 3, searchOnEnter: true, mask: MASK_FORMAT.CUENTA },
                        { title: 'Persona', field: 'idPersona', type: 'persona', size: 5 },
                        { title: 'Estado', field: 'idEstadoCuenta', type: 'selectLista', lista: 'EstadoCuenta', presetValue: 2, presetLabel: 'Activa', size: 2 },
                        { title: 'Etiqueta', field: 'etiqueta', type: 'string', size: 2, searchOnEnter: true },
                        { title: 'Condición especial', field: 'idsTipoCondicionEspecial', type: 'entityMultiple', entidad: 'TipoCondicionEspecial', filter: row => !row.idTipoTributo || row.idTipoTributo === TRIBUTO_TYPE.COMERCIOS, size: 6 },
                        { title: 'Recargo descuento', field: 'idsTipoRecargoDescuento', type: 'entityMultiple', entidad: 'TipoRecargoDescuento', size: 6 },
                        { title: 'Número de CUIT', field: 'numeroCuit', type: 'string', size: 2, searchOnEnter: true, mask: MASK_FORMAT.CUIT },
                        { title: 'Rubro', field: 'rubro', type: 'string', size: 4, searchOnEnter: true },
                        { title: 'Nombre fantasía/Razón social', field: 'nombreFantasia', type: 'string', size: 3, searchOnEnter: true },
                        { title: 'Cuenta inmueble', field: 'cuentaInmueble', type: 'string', size: 3, searchOnEnter: true },
                        { title: 'Domicilio', field: 'direccion', type: 'string', size: 6, searchOnEnter: true },
                    ]}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className={'TableCustomBase m-top-20'}
                        columns={tableColumns}
                        data={state.list}
                        restoredPageIndex={restoredPageIndex}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default ComerciosView;

